// hooks/clients/useClients.ts
import { useState, useCallback, useEffect } from "react";
import { Client } from "../../../types/features/Client";
import { clientService } from "../../../services/clientService";
import { PaginatedResponse } from "../../../types/core/PaginatedResponse";
import { sortByDate } from "../../../helpers/sortDataByDate";

export default function useClients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<Client[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  const fetchClients = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response: PaginatedResponse<Client> = await clientService.getClients(page, perPage);

      const sortedClients = sortByDate(response.data, (client) => client.created_at);

      setClients(sortedClients);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des clients");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClientById = useCallback(async (id: string): Promise<Client | null> => {
    setLoading(true);
    setError(null);
    try {
      const client = await clientService.getClientById(id);
      if (!client) setError("Client non trouvé");
      return client;
    } catch (err) {
      setError("Erreur lors du chargement du client");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchClientByPhone = useCallback(async (phone: string): Promise<Client | null> => {
    setSearching(true);
    setError(null);
    try {
      const client = await clientService.searchClientByPhone(phone);
      setSelectedClient(client);
      return client;
    } catch (err) {
      setError("Erreur lors de la recherche");
      console.error(err);
      return null;
    } finally {
      setSearching(false);
    }
  }, []);

  const searchClients = useCallback(
    async (query: string, page: number = 1, perPage: number = 20): Promise<PaginatedResponse<Client>> => {
      setSearching(true);
      setError(null);
      try {
        const response = await clientService.searchClients(query, page, perPage);
        setSearchResults(response.data);
        return response;
      } catch (err) {
        setError("Erreur lors de la recherche");
        console.error(err);
        return {
          current_page: 1,
          data: [],
          first_page_url: "",
          from: null,
          last_page: 1,
          last_page_url: "",
          links: [],
          next_page_url: null,
          path: "",
          per_page: perPage,
          prev_page_url: null,
          to: null,
          total: 0,
        };
      } finally {
        setSearching(false);
      }
    },
    []
  );

  const createClient = useCallback(
    async (client: Omit<Client, "id" | "created_at" | "updated_at">): Promise<Client | null> => {
      setLoading(true);
      setError(null);
      try {
        const newClient = await clientService.createClient(client);
        setClients((prev) => [newClient, ...prev]);
        return newClient;
      } catch (err) {
        setError("Erreur lors de la création du client");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateClient = useCallback(
    async (id: string, data: Partial<Client>): Promise<Client | null> => {
      setLoading(true);
      setError(null);
      try {
        const updatedClient = await clientService.updateClient(id, data);
        setClients((prev) => prev.map((c) => (c.id === id ? updatedClient : c)));
        if (selectedClient?.id === id) setSelectedClient(updatedClient);
        return updatedClient;
      } catch (err) {
        setError("Erreur lors de la mise à jour du client");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [selectedClient]
  );

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSelectedClient(null);
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return {
    clients,
    selectedClient,
    searchResults,
    loading,
    searching,
    error,
    pagination,
    fetchClients,
    fetchClientById,
    searchClientByPhone,
    searchClients,
    createClient,
    updateClient,
    clearSearch,
  };
}
